import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { cardsAPI } from "../../api/modules/cards.module";
import { useMarketPlace } from "../../Contexts/MarketPlace";
import { useAuth } from "../../hooks/useAuth";
import useDebounce from "../../hooks/useDebounce";
import { EVENTS } from "../../analytics/events";
import { track, trackFirstTime } from "../../analytics/track";
import { useFavourites } from "../../favourites/useFavourites";
import MarketplaceView from "./Marketplace.view";
import { CLEARED_FILTERS } from "./components/MarketplaceFilters/filterModel";

/**
 * Marketplace, the container. The query, the fetch race guard, the seller
 * self-listing filter, the URL parameters and the page state.
 *
 * Everything visual lives in `Marketplace.view.jsx`, which is pure, so the
 * state explorer can mount the real screen without any of this running. See
 * `design-system/explorer/registry.js` for that convention.
 *
 * This page no longer emits a single class name from `Marketplace.css` and no
 * longer imports it; nothing else did, so the file is gone. It also no longer
 * mounts `SellerDashboard/Layout`, which is seller-console chrome that had been
 * wrapped around a buying page.
 *
 * The 480px `matchMedia` that used to live here is gone too. It was the fourth
 * competing breakpoint in the app and it decided both "is this mobile" and "are
 * the filters open", which is why resizing a desktop window could silently
 * close the filter rail. The only threshold now is `useViewport()`'s 1024, and
 * it is read where the DOM actually differs.
 */

const PAGE_SIZE = 15;

// Repeat-search detection, per normalised query text, for one tab session.
// A `useRef` on the component resets every time `Marketplace` mounts, so
// leaving the page and coming back would make a genuine repeat search look
// like a first search again -- worse, `trackFirstTime`'s own lifetime dedup
// (see analytics/track.js) then no-ops on that "first" search too, since it
// already fired once, ever. Net result would be a search that emits NEITHER
// event. `sessionStorage`, keyed like this, survives the remount and matches
// the pre-conversion `SearchBar.jsx`'s `recordSearch(query)` almost exactly
// (see `git show 7f3fed8e:.../SearchBar.jsx` lines 16-27).
const SEARCH_HISTORY_KEY = "vaultsa_search_history";

function recordSearch(query) {
  const key = (query || "").trim().toLowerCase();
  if (!key) return 1;
  try {
    const raw = sessionStorage.getItem(SEARCH_HISTORY_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[key] = (map[key] || 0) + 1;
    sessionStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(map));
    return map[key];
  } catch {
    return 1;
  }
}

const SORT_OPTIONS = [
  { value: "l-h", label: "Price: Low to High" },
  { value: "h-l", label: "Price: High to Low" },
  { value: "a-z", label: "Name: A to Z" },
  { value: "z-a", label: "Name: Z to A" },
  { value: "new", label: "Release date: Newest first" },
  { value: "old", label: "Release date: Oldest first" },
];

/**
 * @param {{ variant?: 'browse'|'search' }} props
 *   `search` mounts this same page at `/search?q=`: the query comes from the
 *   URL instead of a box on the page, and the title says what was searched.
 *   Everything else — the rail, the grid, the sort, the pagination — is the
 *   same code, because narrowing a search and narrowing a browse are the same
 *   act and were never worth two implementations.
 */
const Marketplace = ({ variant = 'browse' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    productName,
    filters,
    setFilters,
    sortBy,
    setSortBy,
    searchTerm,
    setSearchTerm,
    page,
    setPage,
    facetScope,
    setFacetScope,
  } = useMarketPlace();
  const { user, isSeller } = useAuth();

  /* The heart. Before this the grid kept its own Set, which persisted nothing
     for anyone; `useFavourites` is localStorage for a guest and the API for a
     signed-in buyer, and this page does not need to know which. */
  const favourites = useFavourites();

  const [viewMode, setViewMode] = useState("grid");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [totalPages, setTotalPages] = useState(1);
  const [totalCards, setTotalCards] = useState(0);

  /* HOW MANY CARDS THE QUERY MATCHED WITH NOTHING BUT AVAILABILITY DROPPED.
     0 means "no catalogue match either", which is the ordinary empty state.

     `available_to_buy` ships ON, so it is the one filter in the rail nobody
     chose — `countAppliedFilters` does not even count it. That made an empty
     result set from it indistinguishable from a query that matched nothing:
     the nav typeahead searches the whole catalogue, so "char" offered six
     cards, and Enter reported "Nothing matches those filters" for filters the
     buyer never applied. Against QA that is 2,344 matches collapsing to 0.
     Only fetched when a query came up empty, so browse pays nothing. */
  const [catalogueMatches, setCatalogueMatches] = useState(0);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  // THE SEARCH TYPEAHEAD IS GONE, DELIBERATELY.
  //
  // The search box filters the grid live off the same debounced term, so a
  // dropdown of matching card names sat on top of a grid already showing those
  // exact cards. It was never refining the query either: picking a suggestion
  // called `handleCardClick`, so its real job was "jump straight to this card",
  // one click saved over finding the same tile in the results underneath it.
  // Not worth a second network round trip per search, or a panel that covers
  // the results the buyer asked for.
  //
  // `SearchBox` is fully controlled (`showSuggestions = suggestions.length > 0
  // && !dismissed`), so passing it nothing is the whole removal — no
  // design-system change, and no other page passed suggestions.
  //
  // What the typeahead fetch DID still own was the three search analytics
  // events, which is why they now hang off the card fetch instead. See
  // `reportSearch` below.
  /* THE RESULTS PAGE TELLS THE CARD WHERE IT CAME FROM.
     A card detail derives its back control from its breadcrumbs, whose parent
     is `/marketplace` — right for a card opened while browsing, wrong for one
     opened out of a search, where "back" meant losing the query and landing on
     an unfiltered marketplace. The results URL carries everything needed to
     return to it (`?q=`), so the click hands it over and `CardDetail` prefers
     it over the crumb.

     Only from `/search`, and only as a location `state`: a deep link, a
     refresh or a new tab arrives without it and falls back to the crumb, which
     is why this can never strand anyone on a back button pointing nowhere. */
  const handleCardClick = (cardId) => {
    navigate(`/marketplace/card/${cardId}`, {
      state:
        variant === "search"
          ? { from: `${location.pathname}${location.search}`, fromLabel: "search results" }
          : undefined,
    });
  };

  const handleSortChange = (val) => {
    setSortBy(val);
    setPage(1); // reset to first page on sort change
  };

  const handleFilterChange = (newFilters, { dropQuery = false } = {}) => {
    setFilters(newFilters);
    setPage(1);

    const params = new URLSearchParams();
    /* THE QUERY SURVIVES A FACET CHANGE. On `/search` the URL's `q` IS the
       search — the effect below reads it straight back into `searchTerm` — and
       this rebuilds the query string from scratch, so narrowing by rarity, set
       or product type silently dropped the query and dumped the buyer on an
       unfiltered marketplace mid-refinement. Browse has no `q` to carry, so
       this is a no-op there. */
    const carriedQuery = searchParams.get("q");
    if (variant === "search" && carriedQuery && !dropQuery) {
      params.set("q", carriedQuery);
    }
    if (newFilters.set) {
      params.set("set", newFilters.set);
    }
    if (newFilters.catalog_group_id) {
      params.set("catalog_group", newFilters.catalog_group_id);
    }
    if (newFilters.tcg_category_id) {
      params.set("tcg_category_id", newFilters.tcg_category_id);
    }
    if (newFilters.productType && newFilters.productType !== "cards") {
      params.set("productType", newFilters.productType);
    }
    setSearchParams(params, { replace: true });
  };

  /* CLEAR EVERYTHING THE BUYER NARROWED WITH, not just the facets.
     `isFiltered` on the view counts the search term too, so a query that
     excluded every card raised the filtered empty state with no facets applied
     -- and its action cleared filters that were already clear, which did
     nothing. The search term is the other half of "filtered" and it lives in
     `MarketPlaceContext`, so the reset has to reach both.

     No navigation: `/` and `/marketplace` render THIS component, so a cleared
     marketplace already is the home page. `handleFilterChange` rebuilds the
     query string from the cleared filters, which drops `set`, `catalog_group`,
     `tcg_category_id`, `productType` and `q` in one go -- so the address bar
     ends up clean without a route change.

     Sort is deliberately untouched. It orders the results; it does not exclude
     any, so it is not something "clear filters" has taken away. */
  const handleClearAll = () => {
    setSearchTerm("");
    // The ONE caller that means to lose the query: "clear search and filters"
    // says so on the button. Everything else narrowing a search keeps it.
    handleFilterChange(CLEARED_FILTERS, { dropQuery: true });
  };

  /* The way out of "nobody is selling that yet", and the point is what it does
     NOT touch: the query. `handleClearAll` above throws the search away, which
     is the wrong escape from a search that DID match — the buyer wants those
     cards, just not the availability gate in front of them. */
  const handleIncludeUnlisted = () => {
    handleFilterChange({ ...filters, available_to_buy: false });
  };

  /* THE FACETS ARE PART OF ONE SEARCH, AND A NEW SEARCH DOES NOT INHERIT THEM.

     `filters` lives in `MarketPlaceContext` so a browse survives the trip to a
     card detail and back, and `/sets` enters browse at
     `/marketplace?catalog_group=<id>`, which writes `catalog_group_id` into it.
     But `/marketplace` and `/search` are THIS component -- React reuses the
     instance across the two routes, so nothing remounts on the way to the
     results page -- and the effect below only ever MERGED what the URL
     carried. A `/search?q=` URL carries no facets, so the last set or game
     browsed stayed silently applied to a search that never asked for it:
     the nav typeahead searches the whole catalogue and offers the card, Enter
     reports nothing, and the rail names a game the buyer never chose here.
     (`Marketplace.searchleak.test.jsx` is the same leak in the other
     direction, one layer down: the search TERM following you into browse.)

     So on the results page the URL is the whole story, and `facetScope` --
     the `q` these facets were chosen under -- is how a REFINEMENT of the
     search on screen is told apart from a LEFTOVER of the page before it.
     Same query, the buyer's own narrowing, kept. Different query, a new
     search, cleared back to `CLEARED_FILTERS` plus whatever the results URL
     itself carries.

     Browse keeps the plain merge it always had: its own facets are exactly
     what the context is for, and it releases the scope on the way in so a
     later search cannot mistake them for its own. */
  const urlQuery = searchParams.get("q") || "";

  useEffect(() => {
    const setParam = searchParams.get("set");
    const catalogGroupParam = searchParams.get("catalog_group");
    const productTypeParam = searchParams.get("productType");
    const tcgCategoryParam = searchParams.get("tcg_category_id");

    if (variant !== "search") {
      if (facetScope !== null) setFacetScope(null);
    } else if (facetScope !== urlQuery) {
      setFacetScope(urlQuery);
      setFilters({
        ...CLEARED_FILTERS,
        set: setParam || CLEARED_FILTERS.set,
        catalog_group_id: catalogGroupParam || CLEARED_FILTERS.catalog_group_id,
        productType: productTypeParam || CLEARED_FILTERS.productType,
        tcg_category_id: tcgCategoryParam || CLEARED_FILTERS.tcg_category_id,
      });
      return;
    }

    if (setParam || catalogGroupParam || productTypeParam || tcgCategoryParam) {
      setFilters(prev => ({
        ...prev,
        set: setParam || prev.set,
        catalog_group_id: catalogGroupParam || prev.catalog_group_id,
        productType: productTypeParam || prev.productType,
        tcg_category_id: tcgCategoryParam || prev.tcg_category_id,
      }));
    }
    // `setFilters` and `setFacetScope` are context setters and stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, variant, urlQuery, facetScope]);

  // Monotonic id for each fetch. On mount two fetches race: an initial one
  // with the default (empty) filters, then one after the URL->filters effect
  // applies `catalog_group` etc. Without this guard, whichever response
  // resolves LAST wins — so a slow "all cards" response can clobber the
  // filtered result, making the set filter appear and then vanish. We only
  // let the most recent fetch write state.
  const fetchSeqRef = useRef(0);

  // The last search term the analytics have already reported. The card fetch
  // re-runs on page, filter and sort changes as well as on a new query, so
  // without this every page-two click would emit a fresh SEARCH_PERFORMED for
  // a search the buyer ran once.
  const reportedSearchRef = useRef(null);

  /**
   * The three search analytics events, fired off the RESULTS rather than off
   * the typeahead that used to carry them.
   *
   * `result_count` is now the real total the buyer was shown, not the length
   * of a separate typeahead response that nobody ever saw. Property names and
   * values are otherwise verbatim from the pre-conversion `SearchBar.jsx`'s
   * `fireSearchEvent` (`git show 95aac523:apps/web/src/components/UI/SearchBar/
   * SearchBar.jsx`, lines ~100-113), so dashboards keyed on the old names
   * (`query`, `search_type`, `result_count`) keep working untouched.
   *
   * ONLY ON A SUCCESSFUL FETCH, and that is a change worth naming. The old
   * placement fired even when its fetch threw, because that fetch was
   * incidental to the search. Here the fetch IS the search, so a failed one
   * has no result count to report — emitting `result_count: 0` for a request
   * that errored would record "this query matched nothing", which is a
   * different and untrue fact. A buyer whose search fails now produces an
   * error state and no search event.
   */
  const reportSearch = (query, resultCount) => {
    if (!query || reportedSearchRef.current === query) return;
    reportedSearchRef.current = query;

    const searchEventProps = {
      query,
      search_type: "card_name",
      result_count: resultCount,
    };
    track(EVENTS.SEARCH_PERFORMED, searchEventProps);

    // `trackFirstTime` dedups FIRST_SEARCH_PERFORMED for the milestone's whole
    // lifetime via its own localStorage key, so it's safe -- and necessary,
    // see the comment above `recordSearch` -- to call on every search.
    // SEARCH_REPEATED gets its own, independent per-query count. Milestone key
    // is "first_search_performed" (== the event name), same as every other
    // `trackFirstTime` caller in the tree (`CardDetail.jsx`,
    // `CardDetail/CardDetail.jsx`) and as baseline -- anything else means a
    // buyer who already hit this milestone pre-conversion fires it again.
    trackFirstTime(
      "first_search_performed",
      EVENTS.FIRST_SEARCH_PERFORMED,
      searchEventProps
    );

    const searchCount = recordSearch(query);
    if (searchCount > 1) {
      track(EVENTS.SEARCH_REPEATED, { query, search_count: searchCount });
    }
  };

  // Fetch cards with pagination
  const fetchCards = async () => {
    const fetchSeq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);

    try {
      // Prepare API parameters
      const apiParams = {
        category: filters.category,
        search: debouncedSearchTerm || productName,
        min_price: filters.priceRange[0],
        max_price: filters.priceRange[1],
        sort: sortBy,
        page_size: PAGE_SIZE,
        page: page,
      };

      // Pass rarity and condition as arrays directly
      if (filters.rarity && filters.rarity.length > 0) {
        apiParams.rarity = filters.rarity;
      }

      // Pass condition as array directly
      if (filters.condition && filters.condition.length > 0) {
        apiParams.condition = filters.condition;
      }

      // Pass type as array directly
      if (filters.type && filters.type.length > 0) {
        apiParams.type = filters.type;
      }

      // Pass set filter
      if (filters.set) {
        apiParams.set = filters.set;
      }

      // Pass catalog_group filter (same as set but for catalog groups)
      if (filters.catalog_group_id) {
        apiParams.catalog_group_id = filters.catalog_group_id;
      }

      // Pass tcg_category_id filter
      if (filters.tcg_category_id) {
        apiParams.tcg_category_id = filters.tcg_category_id;
      }

      if (filters.productType === "card") {
        apiParams.card = true;
      } else if (filters.productType === "sealed") {
        apiParams.sealed = true;
      }

      if (filters.available_to_buy) {
        apiParams.available_to_buy = true;
      }

      const data = await cardsAPI.getLookUpCards(apiParams);

      // A newer fetch started while this one was in flight — discard this
      // (now stale) response so it can't overwrite the latest results.
      if (fetchSeq !== fetchSeqRef.current) return;

      let filteredCards = data?.data || [];

      if (isSeller && user) {
        const userIds = [
          user.id,
          user._id,
          user.seller_id,
          user.seller?.id,
          user.sellerAccount?.id,
        ].filter(Boolean);

        filteredCards = filteredCards.filter((card) => {
          const cardSellerIds = [
            card.seller_id,
            card.seller?.id,
            card.seller?._id,
          ].filter(Boolean);

          return !cardSellerIds.some((sellerId) => userIds.includes(sellerId));
        });
      }

      setCards(filteredCards);
      setTotalCards(data?.total || 0);
      setTotalPages(Math.ceil((data?.total || 0) / PAGE_SIZE));

      reportSearch(debouncedSearchTerm, data?.total || 0);

      /* Empty, and the only thing narrowing it might be the availability
         default: ask again without it purely for the COUNT, so the empty
         state can tell the buyer whether their query matched nothing or
         matched plenty that nobody has listed. Page size 1 — this needs a
         total, not rows. A failure here just leaves the ordinary empty
         state, so it is deliberately not allowed to raise. */
      if ((data?.total || 0) === 0 && apiParams.available_to_buy) {
        const { available_to_buy: _dropped, ...withoutAvailability } = apiParams;
        try {
          const all = await cardsAPI.getLookUpCards({
            ...withoutAvailability,
            page: 1,
            page_size: 1,
          });
          if (fetchSeq !== fetchSeqRef.current) return;
          setCatalogueMatches(all?.total || 0);
        } catch {
          if (fetchSeq !== fetchSeqRef.current) return;
          setCatalogueMatches(0);
        }
      } else {
        setCatalogueMatches(0);
      }
    } catch (err) {
      if (fetchSeq !== fetchSeqRef.current) return;
      setError("Failed to load cards. Please try again.");
      setCards([]);
    } finally {
      // Only the latest fetch owns the loading flag; a stale one clearing it
      // would hide the spinner while the current request is still running.
      if (fetchSeq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchCards();
  }, [page, filters, debouncedSearchTerm, productName, sortBy]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  // On `/search` the QUERY IS THE URL. The nav box writes `?q=`, this reads
  // it, and the two stay in step through the address bar — which is what makes
  // a result set linkable, shareable and survivable across a refresh. The
  // browse route never touches it.
  useEffect(() => {
    if (variant === "search") {
      setSearchTerm(urlQuery);
      setPage(1);
      return;
    }

    /* BROWSE OWNS NO QUERY, AND HAS TO SAY SO OUT LOUD.
       `searchTerm` lives in `MarketPlaceContext` so browse state survives a
       trip to a card detail and back, and the branch above writes `?q=` into
       that same term. But `/search` and `/marketplace` are THIS component --
       React reuses the instance across the two routes, so nothing remounts and
       nothing refetches -- and browse used to leave the term exactly where the
       search page left it. Leaving results by the card detail's back control
       (whose parent crumb is `/marketplace`, not the results page) therefore
       landed on a marketplace still filtered by the old query, with nothing on
       screen admitting it: browse renders no search box, the nav box only
       shows `q` on `/search`, and the only clear control is the empty state's,
       which needs zero results to appear. A buyer's only way out was to search
       for something else.

       The query belongs to `/search`'s URL. Off that route there isn't one. */
    if (searchTerm) {
      setSearchTerm("");
      // The result set just widened to everything, so page four of the
      // narrowed run means nothing in the new one. Only on an actual clear --
      // an unconditional reset here would throw away the page number that this
      // context exists to carry across a card-detail round trip.
      setPage(1);
    }
    // `setSearchTerm` and `setPage` are context setters and stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, urlQuery, searchTerm]);

  return (
    <MarketplaceView
      variant={variant}
      query={urlQuery}
      cards={cards}
      loading={loading}
      error={error}
      onRetry={fetchCards}
      totalCards={totalCards}
      page={page}
      totalPages={totalPages}
      pageSize={PAGE_SIZE}
      onPageChange={setPage}
      filters={filters}
      onFilterChange={handleFilterChange}
      sortBy={sortBy}
      onSortChange={handleSortChange}
      sortOptions={SORT_OPTIONS}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      onClearAll={handleClearAll}
      /* Where the buyer came FROM, when it was not the nav. The set index
         hands this over on the way into a filtered browse. */
      backTo={location.state?.from}
      backToLabel={location.state?.fromLabel}
      catalogueMatches={catalogueMatches}
      onIncludeUnlisted={handleIncludeUnlisted}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onCardClick={handleCardClick}
      filtersOpen={filtersOpen}
      onOpenFilters={() => setFiltersOpen(true)}
      onCloseFilters={() => setFiltersOpen(false)}
      sortOpen={sortOpen}
      onOpenSort={() => setSortOpen(true)}
      onCloseSort={() => setSortOpen(false)}
      savedIds={favourites.ids}
      onToggleSaved={(cardId) => favourites.toggle(cardId)}
    />
  );
};

export default Marketplace;
